/**
 * SettingsSidebar Component
 * Navigation sidebar for settings sections with grouped items and icons
 */

import {
  Bell,
  ChevronRight,
  Clock,
  FileText,
  HelpCircle,
  Languages,
  Link2,
  LockKeyhole,
  Mail,
  Pencil,
  RectangleEllipsis,
  Smartphone,
  User,
  UserRoundPlus,
  UserRoundX,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import crownIcon from "@/assets/icons/crown.svg"
import moneyIcon from "@/assets/icons/money.svg"
import { cn } from "@/lib"
import type { SettingsNavGroup, SettingsSection } from "../types/settings.types"

export function SettingsSidebar({ activeSection, onSectionChange, onClose }: SettingsSidebarProps) {
  const { t } = useTranslation("settings")
  const navigate = useNavigate()

  const NAV_GROUPS: SettingsNavGroup[] = [
    {
      label: t("sidebar.accountAndSecurity"),
      items: [
        {
          id: "edit-profile",
          label: t("sidebar.editProfile"),
          prefixIcon: <User className="size-5" />,
          shownPostfixIcon: true,
          postfixIcon: <Pencil className="size-5" />,
        },
        {
          id: "change-phone",
          label: t("sidebar.changePhoneNumber"),
          prefixIcon: <Smartphone className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "change-email",
          label: t("sidebar.changeEmailAddress"),
          prefixIcon: <Mail className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "password-security",
          label: t("sidebar.passwordAndSecurity"),
          prefixIcon: <RectangleEllipsis className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "account-privacy",
          label: t("sidebar.accountPrivacy"),
          prefixIcon: <LockKeyhole className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "connected-accounts",
          label: t("sidebar.connectedAccounts"),
          prefixIcon: <Link2 className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "invite-friends",
          label: t("sidebar.inviteFriends"),
          prefixIcon: <UserRoundPlus className="size-5" />,
          shownPostfixIcon: false,
        },
      ],
    },
    {
      label: t("sidebar.payments"),
      items: [
        {
          id: "payments-earnings",
          label: t("sidebar.paymentsAndEarnings"),
          prefixIcon: <img src={moneyIcon} alt="" className="size-5 invert" />,
          shownPostfixIcon: false,
        },
        {
          id: "subscription",
          label: t("sidebar.subscription"),
          prefixIcon: <img src={crownIcon} alt="" className="size-5 invert" />,
          shownPostfixIcon: false,
        },
      ],
    },
    {
      label: t("sidebar.preferences"),
      items: [
        {
          id: "notifications",
          label: t("sidebar.notifications"),
          prefixIcon: <Bell className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "language",
          label: t("sidebar.language"),
          prefixIcon: <Languages className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "timezone",
          label: t("sidebar.timeZone"),
          prefixIcon: <Clock className="size-5" />,
          shownPostfixIcon: false,
        },
      ],
    },
    {
      label: t("sidebar.supportAndLegal"),
      items: [
        {
          id: "help-center",
          label: t("sidebar.helpCenter"),
          prefixIcon: <HelpCircle className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "privacy-policy",
          label: t("sidebar.privacyPolicy"),
          prefixIcon: <FileText className="size-5" />,
          shownPostfixIcon: false,
        },
        {
          id: "remove-account",
          label: t("sidebar.removeAccount"),
          prefixIcon: <UserRoundX className="size-5" />,
          shownPostfixIcon: false,
        },
      ],
    },
  ]

  const handleItemClick = (id: string) => {
    if (id === "edit-profile") {
      navigate("/profile/edit")
      onClose?.()
      return
    }
    onSectionChange(id as SettingsSection)
    onClose?.()
  }

  return (
    <nav className="w-full" aria-label="Settings navigation">
      <div className="flex flex-col gap-6">
        {NAV_GROUPS.map(group => (
          <div key={group.label}>
            <h3 className="text-[18px] font-semibold uppercase tracking-wider px-4 text-primary border-b border-white/5 pb-5">
              {group.label}
            </h3>
            <ul className="flex flex-col">
              {group.items.map(item => {
                const isActive = item.id !== "edit-profile" && activeSection === item.id

                if (item.isSeparator) {
                  return <li key={item.id} className="border-b border-white/5 mx-4 my-2" />
                }

                return (
                  <li key={item.id} className="border-b border-white/5 p-1">
                    <button
                      type="button"
                      onClick={() => handleItemClick(item.id)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
                        isActive
                          ? "bg-beige-medium text-charcoal dark:bg-brand-offwhite-alpha-20 dark:text-brand-offwhite"
                          : "text-stone hover:bg-beige-soft dark:hover:bg-brand-offwhite-alpha-20",
                      )}
                      aria-current={isActive ? "page" : undefined}
                    >
                      <span className="shrink-0 text-stone">{item.prefixIcon}</span>
                      <span className="flex-1 text-left text-primary">{item.label}</span>
                      {item.shownPostfixIcon ? (
                        <span className="shrink-0 text-primary">{item.postfixIcon}</span>
                      ) : (
                        <ChevronRight className="size-5 text-primary shrink-0" />
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  )
}

interface SettingsSidebarProps {
  activeSection: SettingsSection
  onSectionChange: (section: SettingsSection) => void
  onClose?: () => void
}
